/** Configuration navigation panel — source unique pour la sidebar accordion. */

export type NavSubItem = {
  to: string;
  label: string;
  section: string;
  defaultAccess: (r: number) => boolean;
  requireReferentFunction?: string;
  activePrefix?: string;
};

export type NavGroup = {
  id: string;
  label: string;
  icon: string;
  /** Item direct sans dropdown (lien simple). */
  direct?: boolean;
  to?: string;
  section?: string;
  defaultAccess?: (r: number) => boolean;
  activePrefix?: string;
  items?: NavSubItem[];
  /** Section utilisée pour vérifier la visibilité du groupe entier. */
  groupSection?: string;
};

export const PANEL_NAV: NavGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    direct: true,
    to: '/panel/dashboard',
    section: 'stats',
    defaultAccess: (r) => r >= 1 && r <= 6,
    activePrefix: '/panel/dashboard',
  },
  {
    id: 'joueurs',
    label: 'Joueurs',
    icon: 'users',
    items: [
      {
        to: '/panel/joueurs/liste',
        label: 'Liste & comptes',
        section: 'salon_joueurs',
        defaultAccess: (r) => r >= 1 && r <= 4,
      },
      {
        to: '/panel/joueurs/fiches',
        label: 'Fiches détaillées',
        section: 'admin',
        defaultAccess: (r) => r === 1 || r === 2,
      },
      {
        to: '/panel/joueurs/premium',
        label: 'Statut premium',
        section: 'player_premium',
        defaultAccess: (r) => r === 1 || r === 2,
      },
    ],
  },
  {
    id: 'moderation',
    label: 'Modération',
    icon: 'shield',
    direct: true,
    to: '/panel/moderation',
    section: 'moderation',
    defaultAccess: (r) => r >= 1 && r <= 3,
    activePrefix: '/panel/moderation',
  },
  {
    id: 'scenarios',
    label: 'Scénarios & CTA',
    icon: 'cta',
    items: [
      {
        to: '/panel/scenarios/cta',
        label: 'CTA — en cours',
        section: 'interventions',
        defaultAccess: (r) => r >= 1 && r <= 4,
      },
      {
        to: '/panel/scenarios/gerer',
        label: 'Gérer scénarios',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
        activePrefix: '/panel/scenarios/gerer',
      },
      {
        to: '/panel/scenarios/creer',
        label: 'Créer un scénario',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
      },
      {
        to: '/panel/scenarios/categories',
        label: 'Catégories mission',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
      },
      {
        to: '/panel/scenarios/radio',
        label: 'Radio ANTARES',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
      },
    ],
  },
  {
    id: 'logs',
    label: 'Logs & sécurité',
    icon: 'logs',
    items: [
      {
        to: '/panel/logs/synthese',
        label: 'Synthèse',
        section: 'salon_security',
        defaultAccess: (r) => r >= 1 && r <= 3,
      },
      {
        to: '/panel/logs/complet',
        label: 'Logs complets',
        section: 'salon_security',
        defaultAccess: (r) => r >= 1 && r <= 3,
      },
    ],
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: 'settings',
    groupSection: 'system',
    items: [
      {
        to: '/panel/config',
        label: 'Config métier',
        section: 'system',
        defaultAccess: (r) => r === 1,
      },
      {
        to: '/panel/roles/individual',
        label: 'Rôles individuels',
        section: 'roles',
        defaultAccess: (r) => r === 1,
      },
      {
        to: '/panel/roles/default-access',
        label: 'Accès par rôle',
        section: 'admin',
        defaultAccess: (r) => r === 1,
      },
      {
        to: '/panel/users/roles',
        label: 'Rôles utilisateurs',
        section: 'admin',
        defaultAccess: (r) => r === 1,
      },
      {
        to: '/panel/cm/referent',
        label: 'Référent CM',
        section: 'salon_cm',
        defaultAccess: (r) => r === 1,
      },
    ],
  },
  {
    id: 'systeme',
    label: 'Système',
    icon: 'system',
    groupSection: 'system',
    items: [
      {
        to: '/panel/cm/maintenance',
        label: 'Maintenance',
        section: 'salon_cm',
        defaultAccess: (r) => r === 1,
      },
      {
        to: '/panel/system/backup',
        label: 'Sauvegardes',
        section: 'system',
        defaultAccess: (r) => r === 1,
      },
    ],
  },
  {
    id: 'referents',
    label: 'Référents',
    icon: 'referent',
    groupSection: 'salon_ref',
    items: [
      {
        to: '/panel/cm/referent',
        label: 'Référent CM',
        section: 'salon_cm',
        defaultAccess: (r) => r === 1,
        requireReferentFunction: 'Référent CM',
      },
      {
        to: '/panel/users/roles',
        label: 'Référent Admin',
        section: 'admin',
        defaultAccess: (r) => r === 1 || r === 2,
        requireReferentFunction: 'Référent Admin',
      },
      {
        to: '/panel/logs/complet',
        label: 'Référent Modo',
        section: 'salon_security',
        defaultAccess: (r) => r >= 1 && r <= 3,
        requireReferentFunction: 'Référent Modo',
      },
      {
        to: '/panel/scenarios/creer',
        label: 'Référent Scénariste',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
        requireReferentFunction: 'Référent Scénariste',
      },
      {
        to: '/panel/scenarios/gerer',
        label: 'Référent GR',
        section: 'interventions_manage',
        defaultAccess: (r) => r === 1 || r === 4,
        requireReferentFunction: 'Référent GR',
      },
      {
        to: '/panel/support/team',
        label: 'Tickets support',
        section: 'salon_ref',
        defaultAccess: (r) => r === 4 || r === 5,
      },
    ],
  },
];

export const ROLE_LABELS: Record<number, string> = {
  1: 'Community Manager',
  2: 'Administrateur',
  3: 'Modérateur',
  4: 'Groupe de réflexion',
  5: 'Créateur de contenu',
  6: 'Joueur',
};

export function resolvePageTitle(pathname: string): string {
  if (pathname.startsWith('/panel/cm/maintenance')) return 'Maintenance';
  if (pathname.startsWith('/panel/cm/referent')) return 'Référent CM';
  if (pathname.startsWith('/panel/cm')) return 'Espace CM';
  if (pathname.startsWith('/panel/admin')) return 'Espace administrateur';
  if (pathname.startsWith('/panel/modo')) return 'Espace modérateur';
  if (pathname.startsWith('/panel/scenariste')) return 'Espace GR — scénarios';
  if (pathname.startsWith('/panel/ref')) return 'Espace référent';
  if (pathname.startsWith('/panel/dashboard') || pathname === '/panel') return 'Dashboard';
  if (pathname.startsWith('/panel/joueurs/liste')) return 'Joueurs — Liste';
  if (pathname.startsWith('/panel/joueurs/fiches')) return 'Joueurs — Fiches';
  if (pathname.startsWith('/panel/joueurs/premium')) return 'Joueurs — Premium';
  if (pathname.startsWith('/panel/joueurs')) return 'Joueurs';
  if (pathname.startsWith('/panel/scenarios/cta')) return 'Scénarios — CTA';
  if (pathname.startsWith('/panel/scenarios/gerer')) return 'Scénarios — Gérer';
  if (pathname.startsWith('/panel/scenarios/edit')) return 'Scénarios — Modifier';
  if (pathname.startsWith('/panel/scenarios/creer')) return 'Scénarios — Créer';
  if (pathname.startsWith('/panel/scenarios/categories')) return 'Scénarios — Catégories';
  if (pathname.startsWith('/panel/scenarios/radio')) return 'Scénarios — Radio ANTARES';
  if (pathname.startsWith('/panel/scenarios')) return 'Scénarios & CTA';
  if (pathname.startsWith('/panel/logs/synthese')) return 'Logs — Synthèse';
  if (pathname.startsWith('/panel/logs/complet')) return 'Logs — Complet';
  if (pathname.startsWith('/panel/logs')) return 'Logs & sécurité';
  if (pathname.startsWith('/panel/support/team')) return 'Tickets équipe';
  if (pathname.startsWith('/panel/users/roles')) return 'Rôles utilisateurs';
  if (pathname.startsWith('/panel/roles/default-access')) return 'Accès par défaut (rôles)';
  if (pathname.startsWith('/panel/roles/individual')) return 'Rôles individuels';
  if (pathname.startsWith('/panel/moderation/manage')) return 'Gestion joueur';
  if (pathname.startsWith('/panel/moderation')) return 'Modération';
  if (pathname.startsWith('/panel/system/backup')) return 'Sauvegardes';
  if (pathname.startsWith('/panel/config')) return 'Configuration métier';
  return 'Panel';
}

// Compat — anciens types exportés pour éviter de casser les imports existants
export type PanelNavItemConfig = NavSubItem;
export type PanelNavGroupConfig = NavGroup;
export type PanelSalonConfig = {
  id: string;
  label: string;
  subtitle: string;
  to: string;
  section: string;
  accent: 'amber' | 'sky' | 'yellow' | 'violet' | 'cyan';
  defaultAccess: (roleId: number) => boolean;
  loadingFallback: (roleId: number) => boolean;
};
/** @deprecated Utilisez PANEL_NAV */
export const PANEL_NAV_GROUPS: NavGroup[] = [];
/** @deprecated Utilisez PANEL_NAV */
export const PANEL_SALONS: PanelSalonConfig[] = [];
